import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  countryName,
  countryOptions,
  searchCountries,
} from "@/lib/nationality";

/** cmdk value of the optional "not specified" row; never a real country code. */
const NONE_VALUE = "__none__";

interface NationalityComboboxProps {
  id?: string;
  /** ISO 3166-1 alpha-2 code, or "" when nothing is chosen. */
  value: string;
  onChange: (code: string) => void;
  language: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  /**
   * When set, a first row with this label lets the user clear the choice
   * (onChange("")). The application form leaves it off — nationality is
   * required there — and the admin editor turns it on.
   */
  clearLabel?: string;
  className?: string;
}

/**
 * Searchable country picker (COL-618): a Popover holding a shadcn Command, with
 * Deutschland first and the rest alphabetical in the shown language. Typing
 * filters — names that start with the query rank above names that merely
 * contain it, and the English name matches too — Enter picks the highlighted
 * first match, and Escape closes the list.
 *
 * Filtering and ordering are done here (shouldFilter={false}), not by cmdk:
 * cmdk re-sorts the live DOM on every keystroke and keeps the previous
 * keystroke's order on ties, so "the first match" would depend on what was typed
 * before. searchCountries is a pure function of the query, and cmdk only
 * highlights the first row it is given.
 */
export function NationalityCombobox({
  id,
  value,
  onChange,
  language,
  placeholder,
  searchPlaceholder,
  emptyText,
  clearLabel,
  className,
}: NationalityComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const options = useMemo(() => countryOptions(language), [language]);
  const visible = useMemo(
    () => searchCountries(options, query),
    [options, query]
  );
  const showClearRow = !!clearLabel && !query.trim();

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Every opening starts from the full list.
    if (!next) setQuery("");
  };

  const select = (code: string) => {
    onChange(code);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">
            {value ? countryName(value, language) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {visible.length === 0 && (
              <div className="py-6 text-center text-sm">{emptyText}</div>
            )}
            <CommandGroup>
              {showClearRow && (
                <CommandItem
                  value={NONE_VALUE}
                  onSelect={() => select("")}
                  className="text-muted-foreground"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value ? "opacity-0" : "opacity-100"
                    )}
                  />
                  {clearLabel}
                </CommandItem>
              )}
              {visible.map((option) => (
                <CommandItem
                  key={option.code}
                  value={option.code}
                  onSelect={() => select(option.code)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
